import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, RotateCcw, Target, TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Eyebrow } from "./e360Primitives";

const CADENCES = [
  { v: 30, l: "Monthly", label: "Monthly (30d)" },
  { v: 90, l: "Quarterly", label: "Quarterly (90d)" },
  { v: 180, l: "Half-yearly", label: "Half-yearly (180d)" },
  { v: 365, l: "Annually", label: "Annually (365d)" },
];

const StatusBadge = ({ s }) => {
  if (!s?.scheduled) return <span className="text-[12px] rounded-full px-3 py-1 border border-zinc-300 text-zinc-500">Cadence not set</span>;
  const cls = s.overdue ? "text-red-700 bg-red-50 border-red-200" : s.due ? "text-amber-700 bg-amber-50 border-amber-200" : "text-emerald-700 bg-emerald-50 border-emerald-200";
  const label = s.overdue ? `Re-score overdue by ${Math.abs(s.days_until)}d` : s.due ? "Re-score due now" : `Next re-score in ${s.days_until}d`;
  return <span data-testid="reassess-status" className={`text-[12px] rounded-full px-3 py-1 border font-semibold ${cls}`}>● {label}</span>;
};

const Mover = ({ m }) => {
  const up = m.change > 0, flat = m.change === 0;
  const Icon = up ? TrendingUp : flat ? Minus : TrendingDown;
  const cls = up ? "text-emerald-600" : flat ? "text-zinc-400" : "text-red-600";
  return <div className="flex items-center justify-between text-[12px] py-1"><span className="text-zinc-700">{m.sub_brand}</span>
    <span className={`inline-flex items-center gap-1 font-medium ${cls}`}><Icon size={13} /> {m.change > 0 ? "+" : ""}{m.change}</span></div>;
};

export default function ReassessmentPanel({ clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sponsor, setSponsor] = useState("");
  const [notify, setNotify] = useState(false);
  const load = useCallback(async () => {
    if (!clientId) return;
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/reassessment`); setData(data); setSponsor(data.sponsor_email || ""); }
    catch { /* silent */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const setCadence = async (v) => {
    setBusy(true);
    try { const { data } = await api.put(`/admin/enterprise360/clients/${clientId}/reassessment/cadence`, { cadence_days: v }); setData(data); toast.success("Cadence set"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/reassessment/run`, { notify_sponsor: notify, sponsor_email: sponsor });
      setData(data); setSponsor(data.sponsor_email || sponsor);
      toast.success(notify && sponsor ? "Reassessment complete · sponsor emailed" : "Reassessment complete");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  if (!data) return null;
  const latest = data.latest;
  const priCls = { high: "text-red-600", medium: "text-amber-600", low: "text-zinc-500" };
  return (
    <div data-testid="reassessment-panel" className="border border-zinc-200 rounded-lg p-4 bg-white mb-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <Eyebrow><RotateCcw size={13} className="inline mr-1" /> Reassessment loop</Eyebrow>
          <p className="text-[12px] text-zinc-500 -mt-1">Re-score on cadence, see what moved, and get the focus for the next diagnostic — the Optimise → Diagnose bridge.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge s={data.status} />
          <select data-testid="reassess-cadence" className="border border-zinc-300 rounded px-2 py-1 text-[12px]" value={data.cadence_days} onChange={(e) => setCadence(Number(e.target.value))} disabled={busy}>
            {CADENCES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <button data-testid="reassess-run-btn" onClick={run} disabled={busy}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase text-white bg-[var(--e360-primary)] px-3 py-1.5 rounded disabled:opacity-40">
            <RefreshCw size={13} /> Run reassessment
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3 text-[12px] text-zinc-500" data-testid="reassess-sponsor-row">
        <label className="inline-flex items-center gap-1.5">
          <input data-testid="reassess-notify" type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Email sponsor a cycle summary on run
        </label>
        <input data-testid="reassess-sponsor-email" type="email" className="border border-zinc-300 rounded px-2 py-1 text-[12px] w-56" placeholder="sponsor@company.com" value={sponsor} onChange={(e) => setSponsor(e.target.value)} />
      </div>

      {!latest ? (
        <p className="text-[13px] text-zinc-500 py-4">No reassessment cycles yet. Run one to capture the baseline and start the loop.</p>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1">Cycle {latest.cycle_no} · Estate index</div>
            <div className="text-3xl font-semibold text-zinc-900">{latest.estate_index ?? "—"}</div>
            {latest.delta != null && <div className={`text-[13px] font-medium ${latest.delta > 0 ? "text-emerald-600" : latest.delta < 0 ? "text-red-600" : "text-zinc-400"}`}>{latest.delta > 0 ? "▲ +" : latest.delta < 0 ? "▼ " : "– "}{latest.delta} since last cycle</div>}
            <div className="text-[11px] text-zinc-400 mt-2">Carried: {latest.carried?.reds || 0} red risk(s) · {latest.carried?.overdue_gates || 0} gate(s) overdue · {latest.carried?.outstanding_decisions || 0} decision(s) open</div>
          </div>
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1">What moved</div>
            {(latest.movers || []).length === 0 ? <p className="text-[12px] text-zinc-400">No prior cycle to compare.</p>
              : <div data-testid="reassess-movers" className="divide-y divide-zinc-100">{latest.movers.slice(0, 6).map((m) => <Mover key={m.sub_brand} m={m} />)}</div>}
          </div>
          <div className="border border-zinc-200 rounded-lg p-3">
            <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400 mb-1"><Target size={11} className="inline mr-1" /> Focus for next diagnostic</div>
            {(latest.focus_next || []).length === 0 ? <p className="text-[12px] text-zinc-400">Nothing flagged — programme on track.</p>
              : <ul data-testid="reassess-focus" className="space-y-1.5">{latest.focus_next.map((f, i) => (
                  <li key={i} className="text-[12px]"><span className={`font-medium ${priCls[f.priority] || ""}`}>{f.area}</span><span className="text-zinc-500"> — {f.reason}</span></li>))}
                </ul>}
          </div>
        </div>
      )}
      {(data.cycles || []).length > 1 && (
        <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px] text-zinc-400" data-testid="reassess-history">
          <span className="tracking-[0.12em] uppercase">History:</span>
          {data.cycles.map((c) => <span key={c.cycle_no}>C{c.cycle_no}: {c.estate_index ?? "—"}{c.delta != null ? ` (${c.delta > 0 ? "+" : ""}${c.delta})` : ""}</span>)}
        </div>
      )}
    </div>
  );
}
